/**
 * nuclear.js --- Parser for odd article instances
 *
 * The Nuclear Option comes in handy when extreme rich text formatting is necessary
 * (see http://archive.wetink.org/#/archive01/reflections-on-building-a-personal-language)
 *
 * NuclearOption is a parser for every article and editorial body. It takes
 * a slug and all of the rich text that needs to be formatted, and returns
 * correct formatting.
 */

import React from "react"
import { INLINES, BLOCKS, MARKS } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"

import * as Options from "./options"

export function Option(props) {
  // Differentiate between defautl Editorial and Article options
  let mainBodyContent = {}
  let option = props.contentType

  if (option === "Editorial") {
    // Create new content object with parsed JSON fields
    mainBodyContent = {
      content: documentToReactComponents(props.content, editorialOptions()),
      contentType: props.contentType,
      date: props.date,
      dropcap: props.dropcap,
      color: props.color,
      footer: documentToReactComponents(props.footer),
      slug: props.slug,
      title: props.title,
    }
  } else if (option === "Article") {
    // Create new content object with parsed JSON fields
    mainBodyContent = {
      author: props.author,
      color: props.color,
      content: documentToReactComponents(
        props.content,
        articleOptions(props.color, props.slug)
      ),
      contentType: props.contentType,
      date: props.date,
      dropcap: props.dropcap,
      footer: documentToReactComponents(
        props.footer,
        articleOptions(props.color, props.slug)
      ),
      headshots: props.headshots,
      slug: props.slug,
      subtitle: documentToReactComponents(
        props.subtitle,
        subtitleOption(props.color)
      ),
      title: props.title,
    }
  }

  return mainBodyContent
}

/* ===================================================*/

/**
 * editorialOptions: Default editorial option. These include rich text
 * customizations for the letter from the editor. Includes hyperlinks,
 * image links, etc.
 */
const editorialOptions = () => {
  const editorialNode = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        // The name of the type of embedded entry
        let embeddedType = node.data.target.sys.contentType.sys.id

        if (embeddedType === "imageLink")
          return <Options.ImageLink fields={node.data.target.fields} />
      },
      [INLINES.EMBEDDED_ENTRY]: node => {
        // The name of the type of embedded entry
        let embeddedType = node.data.target.sys.contentType.sys.id

        if (embeddedType === "coloredLink") {
          return (
            <Options.ColoredLink
              url={node.data.target.fields.url["en-US"]}
              linkText={node.data.target.fields.linkText["en-US"]}
              color={node.data.target.fields.color["en-US"]}
            />
          )
        }
      },
    },
  }
  return editorialNode
}

/**
 * articleOptions: Default article option. These are rich text
 * customizations for the articles. Includes hyperlinks, image links,
 * embedded bandcamp, vimeo, youtube, etc.
 */
// TODO 3: Clean this up
const articleOptions = (color, slug) => {
  /**
   * articleNode: The default article options
   */
  const articleNode = {
    renderNode: {
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <Options.ColoredLink
            url={node.data.uri}
            linkText={children}
            color={color}
          />
        )
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        return (
          <center>
            <h3 style={{ color: color }}>{children}</h3>
          </center>
        )
      },
      [BLOCKS.OL_LIST]: (node, children) => {
        return (
          <>
            <span style={{ listStyleType: "decimal" }}>
                {children}
            </span>
          </>
        )
      },
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        // The name of the type of embedded entry
        let embeddedType = node.data.target.sys.contentType.sys.id

        if (embeddedType === "imageLink")
          return <Options.ImageLink fields={node.data.target.fields} />
        else if (embeddedType === "embeddedVimeo")
          return (
            <Options.EmbeddedVimeo
              videoTitle={node.data.target.fields.title}
              videoUrl={node.data.target.fields.videoUrl["en-US"]}
              videoDescription={
                node.data.target.fields.videoDescription["en-US"]
              }
              videoColor={color}
            />
          )
        else if (embeddedType === "indent")
          return (
            <div style={{ marginLeft: "6vw" }}>
              {documentToReactComponents(node.data.target.fields.body["en-US"])}
            </div>
          )
        else if (embeddedType === "embeddedYouTube")
          return (
            <Options.EmbeddedYouTube
              videoTitle={node.data.target.fields.title}
              videoUrl={node.data.target.fields.videoUrl["en-US"]}
              videoDescription={
                node.data.target.fields.videoDescription["en-US"]
              }
              videoColor={color}
            />
          )
        else if (embeddedType === "embeddedBandcamp")
          return (
            <Options.EmbeddedBandcamp
              bandcampTitle={node.data.target.fields.title}
              bandcampUrl={node.data.target.fields.url["en-US"]}
              bandcampColor={color}
              bandcampCaption={node.data.target.fields.caption == null ? null : node.data.target.fields.caption["en-US"]}
            />
          )
        else if (embeddedType === "embeddedHtml")
          return (
            <Options.EmbeddedHTML
              title={node.data.target.fields.title}
              url={node.data.target.fields.url["en-US"]}
              color={color}
              caption={node.data.target.fields.caption == null ? null : node.data.target.fields.caption["en-US"]}
            />
          )
        else if (embeddedType === "embeddedSoundCloud")
          return (
            <Options.EmbeddedSoundCloud
              soundcloudUrl={node.data.target.fields.url["en-US"]}
              soundcloudColor={color}
              soundcloudCaption={node.data.target.fields.caption == null ? null : node.data.target.fields.caption["en-US"]}
            />
          )
        else if (embeddedType === "embeddedImage")
          return (
            <Options.EmbeddedImage
              imageURL={
                node.data.target.fields.image["en-US"].fields.file["en-US"].url
              }
              alt={node.data.target.fields.title["en-US"]}
              imageCaption={
                node.data.target.fields.caption == null
                  ? null
                  : node.data.target.fields.caption["en-US"]
              }
              captionColor={color}
              size={node.data.target.fields.size}
            />
          )
        else if (embeddedType === "borderedVideo")
          return (
            <Options.BorderedVideo
              color={color}
              header={node.data.target.fields.header["en-US"]}
              type={node.data.target.fields.videoType["en-US"]}
              videoId={node.data.target.fields.videoId["en-US"]}
            />
          )
        else if (embeddedType === "corbelText") {
          return (
            <Options.CorbelText
              text={documentToReactComponents(
                node.data.target.fields.text["en-US"]
              )}
              color={node.data.target.fields.color["en-US"]}
            />
          )
        } else if (embeddedType === "centeredText") {
            return (
              <Options.CenteredText
                text={node.data.target.fields.text === undefined
                  ? null
                  : node.data.target.fields.text["en-US"]}
                color={color}
              />
            )
        } else if (embeddedType === "singleSpaceText") {
          return (
            <Options.SingleSpaceText
              text={documentToReactComponents(
                node.data.target.fields.text["en-US"]
              )}
            />
          )
        } else if (embeddedType === "subArticleTitle") {
          return (
            <Options.SubArticleTitle
              title={
                node.data.target.fields.title === undefined
                  ? null
                  : node.data.target.fields.title["en-US"]
              }
              author={
                node.data.target.fields.author === undefined
                  ? null
                  : node.data.target.fields.author["en-US"]
              }
              authorSubtitle={
                node.data.target.fields.authorSubtitle === undefined
                  ? null
                  : node.data.target.fields.authorSubtitle["en-US"]
              }
              color={color}
            />
          )
        }
      },

      [BLOCKS.QUOTE]: (node, children) => {
        return <Options.BlockQuote children={children} />
      },
    },
    // Bold text takes default article color
    renderMark: {
      [MARKS.BOLD]: children => {
        return <b style={{ color: color }}>{children}</b>
      },
    },
  }

  return articleNode
}

/**
 * subtitleOption: Default option for the subtitle. Includes
 * recognition of <i>, <b>, <u>, and images
            */
const subtitleOption = color => {
  const subtitleNode = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        // The name of the type of embedded entry
        let embeddedType = node.data.target.sys.contentType.sys.id

        if (embeddedType === "embeddedImage") {
          return (
            <Options.EmbeddedImage
              imageURL={
                node.data.target.fields.image["en-US"].fields.file["en-US"].url
              }
              alt={node.data.target.fields.title["en-US"]}
              imageCaption={
                node.data.target.fields.caption == null
                  ? null
                  : node.data.target.fields.caption["en-US"]
              }
              captionColor={color}
              size={node.data.target.fields.size}
            />
          )
        }
      },
    },
  }
  return subtitleNode
}
